import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowUpDown, Eye, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Lead, LeadStatus } from "@/lib/types";
import { timeAgo, fullDate } from "@/lib/timeAgo";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { LeadChip, PlatformChip } from "@/components/shared/Chips";
import { PagedToolbar, Pagination } from "@/components/shared/Pagination";
import { Customer360Sheet } from "@/components/shared/Customer360Sheet";
import { CaptureLinkDialog } from "@/components/shared/CaptureLinkDialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES: LeadStatus[] = ["new", "contacted", "qualified", "converted", "closed"];

export const Leads: React.FC = () => {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [customer360Handle, setCustomer360Handle] = useState<string | null>(null);
  const [captureLinkFor, setCaptureLinkFor] = useState<Lead | null>(null);
  const sortBy = params.get("sort_by") || "created_at";
  const order = params.get("order") || "desc";
  const platformFilter = params.get("platform") || "all";
  const search = params.get("q") || "";
  const authorFilter = params.get("author_handle") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const pageSize = Math.max(10, Math.min(500, parseInt(params.get("page_size") || "50", 10) || 50));

  const leads = useQuery<{ items: Lead[]; total: number; offset: number; limit: number }>({
    queryKey: ["leads", sortBy, order, platformFilter, search, authorFilter, page, pageSize],
    queryFn: async () => {
      const q = new URLSearchParams();
      q.set("sort_by", sortBy);
      q.set("order", order);
      if (platformFilter !== "all") q.set("platform", platformFilter);
      if (search.trim()) q.set("q", search.trim());
      if (authorFilter.trim()) q.set("author_handle", authorFilter.trim());
      q.set("offset", String((page - 1) * pageSize));
      q.set("limit", String(pageSize));
      return (await api.get(`/leads?${q.toString()}`)).data;
    },
  });

  const items = leads.data?.items || [];
  const total = leads.data?.total || 0;

  const update = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: LeadStatus }) =>
      (await api.patch(`/leads/${id}`, { status })).data,
    onSuccess: () => {
      toast.success("Lead updated");
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  const setParam = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v === null || v === "") p.delete(k); else p.set(k, v);
    p.set("page", "1");
    setParams(p);
  };

  return (
    <DashboardLayout
      title="Leads"
      subtitle="High-intent commenters, ready to be converted."
      seoPath="/leads"
      seoDescription="High-intent commenters captured from your social channels — ready to be converted into customers."
    >
      <PagedToolbar
        testId="leads-search"
        search={search}
        placeholder="Search leads by text, author, or next-step…"
        onSearch={(v) => setParam("q", v)}
        authorFilter={authorFilter || undefined}
        onClearAuthor={() => setParam("author_handle", null)}
      />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={platformFilter} onValueChange={(v) => setParam("platform", v === "all" ? null : v)}>
          <SelectTrigger data-testid="filter-lead-platform" className="w-44 bg-white">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All platforms</SelectItem>
            <SelectItem value="youtube">YouTube</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="linkedin">LinkedIn</SelectItem>
            <SelectItem value="twitter">X / Twitter</SelectItem>
            <SelectItem value="tiktok">TikTok</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={`${sortBy}:${order}`}
          onValueChange={(v) => {
            const [sb, o] = v.split(":");
            const p = new URLSearchParams(params);
            p.set("sort_by", sb);
            p.set("order", o);
            p.set("page", "1");
            setParams(p);
          }}
        >
          <SelectTrigger data-testid="sort-leads" className="w-56 bg-white">
            <ArrowUpDown className="h-3.5 w-3.5 text-[color:var(--sg-muted)] mr-1" />
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="created_at:desc">Recent first</SelectItem>
            <SelectItem value="created_at:asc">Oldest first</SelectItem>
            <SelectItem value="updated_at:desc">Recently updated</SelectItem>
            <SelectItem value="platform:asc">Channel · A → Z</SelectItem>
            <SelectItem value="platform:desc">Channel · Z → A</SelectItem>
            <SelectItem value="author_handle:asc">Author · A → Z</SelectItem>
            <SelectItem value="intent_score:desc">Intent score (high → low)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {leads.isLoading ? (
        <div className="sg-skeleton h-64" />
      ) : items.length > 0 ? (
        <div className="grid gap-3">
          {items.map((l) => {
            const handle = (l.author_handle || "").replace(/^@/, "");
            return (
              <div key={l.lead_id} className="sg-card p-4" data-testid={`lead-row-${l.lead_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setCustomer360Handle(handle)}
                      data-testid={`lead-author-${l.lead_id}`}
                      className="font-semibold text-sm hover:text-[color:var(--sg-primary)] hover:underline"
                      title={`Customer 360 for ${l.author_handle}`}
                    >
                      {l.author_handle}
                    </button>
                    <PlatformChip platform={l.platform} />
                    <LeadChip status={l.status} />
                  </div>
                  <Select
                    value={l.status}
                    onValueChange={(v) =>
                      update.mutate({ id: l.lead_id, status: v as LeadStatus })
                    }
                  >
                    <SelectTrigger data-testid={`lead-status-${l.lead_id}`} className="w-40 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-[color:var(--sg-fg)] line-clamp-3">
                  {l.original_comment}
                </p>
                <div className="text-[11px] text-[color:var(--sg-muted)] mt-2 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span>Intent {(l.intent_score * 100).toFixed(0)}%</span>
                    <span title={fullDate(l.created_at)} data-testid={`lead-date-${l.lead_id}`}>
                      {timeAgo(l.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setCaptureLinkFor(l)}
                      data-testid={`lead-capture-link-${l.lead_id}`}
                      className="inline-flex items-center gap-1 text-[color:var(--sg-violet)] hover:underline font-medium"
                      title="Send a form to capture this customer's real contact details"
                    >
                      <LinkIcon className="h-3 w-3" />
                      Send capture link
                    </button>
                    <Link
                      to={`/comments?author_handle=${encodeURIComponent(handle)}`}
                      data-testid={`lead-view-comments-${l.lead_id}`}
                      className="inline-flex items-center gap-1 text-[color:var(--sg-primary)] hover:underline font-medium"
                    >
                      <Eye className="h-3 w-3" />
                      View all comments from {l.author_handle}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={search || authorFilter ? "No leads match your filters" : "No leads yet"}
          description={search || authorFilter ? "Try clearing the search or the user filter." : "Once high-intent comments come in they'll show up here."}
        />
      )}

      <Pagination
        testId="leads-pagination"
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={(p) => { const q = new URLSearchParams(params); q.set("page", String(p)); setParams(q); }}
        onPageSizeChange={(s) => { const q = new URLSearchParams(params); q.set("page_size", String(s)); q.set("page", "1"); setParams(q); }}
      />

      <Customer360Sheet
        open={!!customer360Handle}
        onOpenChange={(o) => !o && setCustomer360Handle(null)}
        handle={customer360Handle || ""}
        onFilterHere={
          customer360Handle
            ? () => setParam("author_handle", customer360Handle)
            : undefined
        }
      />

      <CaptureLinkDialog
        open={!!captureLinkFor}
        onOpenChange={(o) => !o && setCaptureLinkFor(null)}
        commentId={captureLinkFor?.comment_id}
      />
    </DashboardLayout>
  );
};
